import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  Switch,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { createLogger } from '../../../../utils/logger';
const logger = createLogger('BodyArrangementsInput');

import {
  TraditionalBurialDetails,
  CremationDetails,
  ScienceDonationDetails,
  GreenBurialDetails,
  MilitaryBurialDetails,
  OtherArrangementDetails,
  ArrangementDetails,
  bodyArrangementsInfoService
} from '../../../../services/bodyArrangementsInfo';

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;
type BodyArrangementsInputScreenRouteProp = RouteProp<RootStackParamList, 'BodyArrangementsInput'>;

type ArrangementType = 'traditional' | 'cremation' | 'science' | 'green' | 'military' | 'other' | null;

export const BodyArrangementsInputScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<BodyArrangementsInputScreenRouteProp>();

  const { bodyArrangementsData, isEditing } = route.params || {};

  const [selectedArrangement, setSelectedArrangement] = useState<ArrangementType>(
    bodyArrangementsData?.arrangementType as ArrangementType || null
  );
  const [specialInstructions, setSpecialInstructions] = useState(
    bodyArrangementsData?.specificInstructions || ''
  );
  const [ashesDisposition, setAshesDisposition] = useState(
    bodyArrangementsData?.ashesDisposition || ''
  );

  // Initialize details with existing data if editing
  const getInitialDetails = () => {
    if (bodyArrangementsData?.arrangementDetails && bodyArrangementsData.arrangementType) {
      return bodyArrangementsData.arrangementDetails;
    }
    return null;
  };

  const initialDetails = getInitialDetails();

  // Traditional Burial Details
  const [traditionalDetails, setTraditionalDetails] = useState<TraditionalBurialDetails>({
    openCasket: (initialDetails as TraditionalBurialDetails)?.openCasket || false,
    burialClothes: (initialDetails as TraditionalBurialDetails)?.burialClothes || '',
    wearGlasses: (initialDetails as TraditionalBurialDetails)?.wearGlasses || false,
    favoriteHatKnickknack: (initialDetails as TraditionalBurialDetails)?.favoriteHatKnickknack || '',
    favoriteShoes: (initialDetails as TraditionalBurialDetails)?.favoriteShoes || '',
  });

  // Cremation Details
  const [cremationDetails, setCremationDetails] = useState<CremationDetails>({
    bodyPreparationMethod: (initialDetails as CremationDetails)?.bodyPreparationMethod || '',
    buryInCasketAfterCremation: (initialDetails as CremationDetails)?.buryInCasketAfterCremation || false,
    sendAshesToSpace: (initialDetails as CremationDetails)?.sendAshesToSpace || false,
  });

  // Science Donation Details
  const [scienceDetails, setScienceDetails] = useState<ScienceDonationDetails>({
    medicalSchoolPreference: (initialDetails as ScienceDonationDetails)?.medicalSchoolPreference || '',
    forensicResearch: (initialDetails as ScienceDonationDetails)?.forensicResearch || false,
    forProfitCompanies: (initialDetails as ScienceDonationDetails)?.forProfitCompanies || false,
    bodyWorldExhibit: (initialDetails as ScienceDonationDetails)?.bodyWorldExhibit || false,
  });

  // Green Burial Details
  const [greenDetails, setGreenDetails] = useState<GreenBurialDetails>({
    noEmbalmingFluids: (initialDetails as GreenBurialDetails)?.noEmbalmingFluids || false,
    biodegradableCasket: (initialDetails as GreenBurialDetails)?.biodegradableCasket || false,
    naturalGrave: (initialDetails as GreenBurialDetails)?.naturalGrave || false,
    mausoleum: (initialDetails as GreenBurialDetails)?.mausoleum || false,
  });

  // Military Burial Details
  const [militaryDetails, setMilitaryDetails] = useState<MilitaryBurialDetails>({
    traditionalMilitaryHonors: (initialDetails as MilitaryBurialDetails)?.traditionalMilitaryHonors || false,
    flagDrapedCasket: (initialDetails as MilitaryBurialDetails)?.flagDrapedCasket || false,
    rifleVolleySalute: (initialDetails as MilitaryBurialDetails)?.rifleVolleySalute || false,
    playingOfTaps: (initialDetails as MilitaryBurialDetails)?.playingOfTaps || false,
    flagPresentation: (initialDetails as MilitaryBurialDetails)?.flagPresentation || false,
  });

  // Other Arrangement Details
  const [otherDetails, setOtherDetails] = useState<OtherArrangementDetails>({
    restAtSea: (initialDetails as OtherArrangementDetails)?.restAtSea || false,
    liquidify: (initialDetails as OtherArrangementDetails)?.liquidify || false,
    crashTestSimulations: (initialDetails as OtherArrangementDetails)?.crashTestSimulations || false,
    returnToSoil: (initialDetails as OtherArrangementDetails)?.returnToSoil || false,
    becomeATree: (initialDetails as OtherArrangementDetails)?.becomeATree || false,
    customDescription: (initialDetails as OtherArrangementDetails)?.customDescription || '',
  });

  const handleSave = async () => {
    try {
      // Determine which arrangement details to save based on selected type
      let arrangementDetails: ArrangementDetails = null;

      switch (selectedArrangement) {
        case 'traditional':
          arrangementDetails = traditionalDetails;
          break;
        case 'cremation':
          arrangementDetails = cremationDetails;
          break;
        case 'science':
          arrangementDetails = scienceDetails;
          break;
        case 'green':
          arrangementDetails = greenDetails;
          break;
        case 'military':
          arrangementDetails = militaryDetails;
          break;
        case 'other':
          arrangementDetails = otherDetails;
          break;
      }

      // Prepare the data to save
      const bodyArrangementsUpdateData = {
        arrangementType: selectedArrangement || '',
        ashesDisposition: ashesDisposition || undefined,
        specificInstructions: specialInstructions,
        arrangementDetails,
        notes: specialInstructions, // Map specialInstructions to notes field
      };

      let savedBodyArrangements;

      if (isEditing && bodyArrangementsData?.id) {
        // Update existing record
        savedBodyArrangements = await bodyArrangementsInfoService.updateBodyArrangementsInfo(
          bodyArrangementsData.id,
          bodyArrangementsUpdateData
        );
        // Handle wrapped response format { success, data, message }
        const updatedData = savedBodyArrangements?.data || savedBodyArrangements;
        logger.debug('Body arrangements updated successfully with ID:', updatedData.id);

        // Navigate to lock/unlock screen to allow re-selection of lock/unlock
        (navigation as any).navigate('BodyArrangementsLockUnlock', { bodyArrangementsId: updatedData.id });
      } else {
        // Create new record
        savedBodyArrangements = await bodyArrangementsInfoService.createBodyArrangementsInfo(bodyArrangementsUpdateData);
        // Handle wrapped response format { success, data, message }
        const createdData = savedBodyArrangements?.data || savedBodyArrangements;
        logger.debug('Body arrangements saved successfully with ID:', createdData.id);

        // Navigate to lock/unlock screen with the ID
        (navigation as any).navigate('BodyArrangementsLockUnlock', { bodyArrangementsId: createdData.id });
      }
    } catch (error) {
      logger.error('Error saving body arrangements:', error);
    }
  };

  const renderDetailsInput = () => {
    switch (selectedArrangement) {
      case 'traditional':
        return (
          <View>
            <Text style={styles.detailsTitle}>Traditional Burial Details:</Text>

            {/* Open Casket Toggle */}
            <View style={styles.toggleRow}>
              <Text style={styles.toggleLabel}>Do you want an open casket?</Text>
              <Switch
                value={traditionalDetails.openCasket}
                onValueChange={(value) =>
                  setTraditionalDetails({ ...traditionalDetails, openCasket: value })
                }
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={traditionalDetails.openCasket ? '#FFFFFF' : '#FFFFFF'}
              />
            </View>

            {/* Burial Clothes Input */}
            <Text style={styles.inputLabel}>What clothes do you want to be buried in?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe your preferred burial clothes..."
              value={traditionalDetails.burialClothes}
              onChangeText={(value) =>
                setTraditionalDetails({ ...traditionalDetails, burialClothes: value })
              }
              multiline
            />

            {/* Glasses Toggle */}
            <View style={styles.toggleRow}>
              <Text style={styles.toggleLabel}>Do you wear glasses?</Text>
              <Switch
                value={traditionalDetails.wearGlasses}
                onValueChange={(value) =>
                  setTraditionalDetails({ ...traditionalDetails, wearGlasses: value })
                }
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={traditionalDetails.wearGlasses ? '#FFFFFF' : '#FFFFFF'}
              />
            </View>

            {/* Favorite Hat/Knick-knack Input */}
            <Text style={styles.inputLabel}>Do you have a favorite hat or knick-knack?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe any favorite items..."
              value={traditionalDetails.favoriteHatKnickknack}
              onChangeText={(value) =>
                setTraditionalDetails({ ...traditionalDetails, favoriteHatKnickknack: value })
              }
              multiline
            />

            {/* Favorite Shoes Input */}
            <Text style={styles.inputLabel}>Do you have a favorite pair of shoes?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe your favorite shoes..."
              value={traditionalDetails.favoriteShoes}
              onChangeText={(value) =>
                setTraditionalDetails({ ...traditionalDetails, favoriteShoes: value })
              }
              multiline
            />
          </View>
        );
      case 'cremation':
        return (
          <View>
            <Text style={styles.detailsTitle}>Cremation Details:</Text>

            {/* Body Preparation Method */}
            <Text style={styles.inputLabel}>How do you want your body prepared?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe how you want your body prepared for cremation..."
              value={cremationDetails.bodyPreparationMethod}
              onChangeText={(value) =>
                setCremationDetails({ ...cremationDetails, bodyPreparationMethod: value })
              }
              multiline
            />

            {/* Ashes Disposition - What to do with ashes */}
            <Text style={styles.inputLabel}>What would you like done with your ashes?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="E.g., scattered in mountains, buried in cemetery, kept by family..."
              value={ashesDisposition}
              onChangeText={setAshesDisposition}
              multiline
            />

            {/* Bury in Casket After Cremation Toggle */}
            <View style={styles.toggleRow}>
              <Text style={styles.toggleLabel}>Do you still want to be buried in a casket after cremation?</Text>
              <Switch
                value={cremationDetails.buryInCasketAfterCremation}
                onValueChange={(value) =>
                  setCremationDetails({ ...cremationDetails, buryInCasketAfterCremation: value })
                }
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={cremationDetails.buryInCasketAfterCremation ? '#FFFFFF' : '#FFFFFF'}
              />
            </View>

            {/* Send Ashes to Space Toggle */}
            <View style={styles.toggleRow}>
              <Text style={styles.toggleLabel}>Place your ashes some place special?</Text>
              <Switch
                value={cremationDetails.sendAshesToSpace}
                onValueChange={(value) =>
                  setCremationDetails({ ...cremationDetails, sendAshesToSpace: value })
                }
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={cremationDetails.sendAshesToSpace ? '#FFFFFF' : '#FFFFFF'}
              />
            </View>
          </View>
        );
      case 'science':
        return (
          <View>
            <Text style={styles.detailsTitle}>Science Donation Details:</Text>

            {/* Medical School Preference */}
            <Text style={styles.inputLabel}>Do you want to be donated to a medical school? If so, which one?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Specify preferred medical school or leave blank..."
              value={scienceDetails.medicalSchoolPreference}
              onChangeText={(value) =>
                setScienceDetails({ ...scienceDetails, medicalSchoolPreference: value })
              }
              multiline
            />

            {/* Forensic Research Toggle */}
            <View style={styles.toggleRow}>
              <Text style={styles.toggleLabel}>Do you want to be donated to forensic research?</Text>
              <Switch
                value={scienceDetails.forensicResearch}
                onValueChange={(value) =>
                  setScienceDetails({ ...scienceDetails, forensicResearch: value })
                }
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={scienceDetails.forensicResearch ? '#FFFFFF' : '#FFFFFF'}
              />
            </View>

            {/* For-profit Companies Toggle */}
            <View style={styles.toggleRow}>
              <Text style={styles.toggleLabel}>Donate to for-profit companies?</Text>
              <Switch
                value={scienceDetails.forProfitCompanies}
                onValueChange={(value) =>
                  setScienceDetails({ ...scienceDetails, forProfitCompanies: value })
                }
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={scienceDetails.forProfitCompanies ? '#FFFFFF' : '#FFFFFF'}
              />
            </View>

            {/* BodyWorld Exhibit Toggle */}
            <View style={styles.toggleRow}>
              <Text style={styles.toggleLabel}>BodyWorld (exhibit)?</Text>
              <Switch
                value={scienceDetails.bodyWorldExhibit}
                onValueChange={(value) =>
                  setScienceDetails({ ...scienceDetails, bodyWorldExhibit: value })
                }
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={scienceDetails.bodyWorldExhibit ? '#FFFFFF' : '#FFFFFF'}
              />
            </View>
          </View>
        );
      case 'green':
        return (
          <View>
            <Text style={styles.detailsTitle}>Green Burial Details:</Text>

            {/* No Embalming Fluids Toggle */}
            <View style={styles.toggleRow}>
              <Text style={styles.toggleLabel}>No embalming fluids?</Text>
              <Switch
                value={greenDetails.noEmbalmingFluids}
                onValueChange={(value) =>
                  setGreenDetails({ ...greenDetails, noEmbalmingFluids: value })
                }
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={greenDetails.noEmbalmingFluids ? '#FFFFFF' : '#FFFFFF'}
              />
            </View>

            {/* Biodegradable Casket Toggle */}
            <View style={styles.toggleRow}>
              <Text style={styles.toggleLabel}>Do you want to be buried in a biodegradable casket?</Text>
              <Switch
                value={greenDetails.biodegradableCasket}
                onValueChange={(value) =>
                  setGreenDetails({ ...greenDetails, biodegradableCasket: value })
                }
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={greenDetails.biodegradableCasket ? '#FFFFFF' : '#FFFFFF'}
              />
            </View>

            {/* Natural Grave Toggle */}
            <View style={styles.toggleRow}>
              <Text style={styles.toggleLabel}>Do you want to be buried in a natural grave?</Text>
              <Switch
                value={greenDetails.naturalGrave}
                onValueChange={(value) =>
                  setGreenDetails({ ...greenDetails, naturalGrave: value })
                }
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={greenDetails.naturalGrave ? '#FFFFFF' : '#FFFFFF'}
              />
            </View>

            {/* Mausoleum Toggle */}
            <View style={styles.toggleRow}>
              <Text style={styles.toggleLabel}>Do you want to be buried in a mausoleum?</Text>
              <Switch
                value={greenDetails.mausoleum}
                onValueChange={(value) =>
                  setGreenDetails({ ...greenDetails, mausoleum: value })
                }
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={greenDetails.mausoleum ? '#FFFFFF' : '#FFFFFF'}
              />
            </View>
          </View>
        );
      case 'military':
        return (
          <View>
            <Text style={styles.detailsTitle}>Military Burial Details:</Text>

            {/* Traditional Military Honors Toggle */}
            <View style={styles.toggleRow}>
              <Text style={styles.toggleLabel}>Traditional military honors?</Text>
              <Switch
                value={militaryDetails.traditionalMilitaryHonors}
                onValueChange={(value) =>
                  setMilitaryDetails({ ...militaryDetails, traditionalMilitaryHonors: value })
                }
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={militaryDetails.traditionalMilitaryHonors ? '#FFFFFF' : '#FFFFFF'}
              />
            </View>

            {/* Flag-draped Casket Toggle */}
            <View style={styles.toggleRow}>
              <Text style={styles.toggleLabel}>Flag-draped casket?</Text>
              <Switch
                value={militaryDetails.flagDrapedCasket}
                onValueChange={(value) =>
                  setMilitaryDetails({ ...militaryDetails, flagDrapedCasket: value })
                }
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={militaryDetails.flagDrapedCasket ? '#FFFFFF' : '#FFFFFF'}
              />
            </View>

            {/* Rifle Volley Salute Toggle */}
            <View style={styles.toggleRow}>
              <Text style={styles.toggleLabel}>Rifle volley (three-volley salute)?</Text>
              <Switch
                value={militaryDetails.rifleVolleySalute}
                onValueChange={(value) =>
                  setMilitaryDetails({ ...militaryDetails, rifleVolleySalute: value })
                }
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={militaryDetails.rifleVolleySalute ? '#FFFFFF' : '#FFFFFF'}
              />
            </View>

            {/* Playing of Taps Toggle */}
            <View style={styles.toggleRow}>
              <Text style={styles.toggleLabel}>The playing of 'Taps'?</Text>
              <Switch
                value={militaryDetails.playingOfTaps}
                onValueChange={(value) =>
                  setMilitaryDetails({ ...militaryDetails, playingOfTaps: value })
                }
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={militaryDetails.playingOfTaps ? '#FFFFFF' : '#FFFFFF'}
              />
            </View>

            {/* Flag Presentation Toggle */}
            <View style={styles.toggleRow}>
              <Text style={styles.toggleLabel}>The presentation of the folded flag?</Text>
              <Switch
                value={militaryDetails.flagPresentation}
                onValueChange={(value) =>
                  setMilitaryDetails({ ...militaryDetails, flagPresentation: value })
                }
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={militaryDetails.flagPresentation ? '#FFFFFF' : '#FFFFFF'}
              />
            </View>
          </View>
        );
      case 'other':
        return (
          <View>
            <Text style={styles.detailsTitle}>Other Arrangement Details:</Text>

            {/* Rest at Sea Toggle */}
            <View style={styles.toggleRow}>
              <Text style={styles.toggleLabel}>Rest at sea?</Text>
              <Switch
                value={otherDetails.restAtSea}
                onValueChange={(value) =>
                  setOtherDetails({ ...otherDetails, restAtSea: value })
                }
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={otherDetails.restAtSea ? '#FFFFFF' : '#FFFFFF'}
              />
            </View>

            {/* Liquidify Toggle */}
            <View style={styles.toggleRow}>
              <Text style={styles.toggleLabel}>Liquidify yourself?</Text>
              <Switch
                value={otherDetails.liquidify}
                onValueChange={(value) =>
                  setOtherDetails({ ...otherDetails, liquidify: value })
                }
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={otherDetails.liquidify ? '#FFFFFF' : '#FFFFFF'}
              />
            </View>

            {/* Crash Test Simulations Toggle */}
            <View style={styles.toggleRow}>
              <Text style={styles.toggleLabel}>Crash test simulations?</Text>
              <Switch
                value={otherDetails.crashTestSimulations}
                onValueChange={(value) =>
                  setOtherDetails({ ...otherDetails, crashTestSimulations: value })
                }
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={otherDetails.crashTestSimulations ? '#FFFFFF' : '#FFFFFF'}
              />
            </View>

            {/* Return to Soil Toggle */}
            <View style={styles.toggleRow}>
              <Text style={styles.toggleLabel}>Return to soil?</Text>
              <Switch
                value={otherDetails.returnToSoil}
                onValueChange={(value) =>
                  setOtherDetails({ ...otherDetails, returnToSoil: value })
                }
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={otherDetails.returnToSoil ? '#FFFFFF' : '#FFFFFF'}
              />
            </View>

            {/* Become a Tree Toggle */}
            <View style={styles.toggleRow}>
              <Text style={styles.toggleLabel}>Become a tree?</Text>
              <Switch
                value={otherDetails.becomeATree}
                onValueChange={(value) =>
                  setOtherDetails({ ...otherDetails, becomeATree: value })
                }
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={otherDetails.becomeATree ? '#FFFFFF' : '#FFFFFF'}
              />
            </View>

            {/* Custom Description */}
            <Text style={styles.inputLabel}>Custom arrangement description:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe any other custom arrangements..."
              value={otherDetails.customDescription}
              onChangeText={(value) =>
                setOtherDetails({ ...otherDetails, customDescription: value })
              }
              multiline
            />
          </View>
        );
      default:
        return null;
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>
          How would you like to be laid to rest after you pass?
        </Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollViewContent} keyboardShouldPersistTaps="handled" automaticallyAdjustKeyboardInsets={true}>
          <View style={styles.radioGroup}>
            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedArrangement('traditional')}
            >
              <View style={styles.radioButton}>
                {selectedArrangement === 'traditional' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>Traditional burial service</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedArrangement('cremation')}
            >
              <View style={styles.radioButton}>
                {selectedArrangement === 'cremation' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>Cremation</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedArrangement('science')}
            >
              <View style={styles.radioButton}>
                {selectedArrangement === 'science' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>Donate my body to science</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedArrangement('green')}
            >
              <View style={styles.radioButton}>
                {selectedArrangement === 'green' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>Green burial</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedArrangement('military')}
            >
              <View style={styles.radioButton}>
                {selectedArrangement === 'military' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>Military burial</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedArrangement('other')}
            >
              <View style={styles.radioButton}>
                {selectedArrangement === 'other' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>Other</Text>
            </TouchableOpacity>
          </View>

          {renderDetailsInput()}

          <Text style={styles.label}>Special instructions:</Text>
          <TextInput
            style={styles.specialInstructionsInput}
            placeholder="Enter any special instructions..."
            value={specialInstructions}
            onChangeText={setSpecialInstructions}
            multiline
          />

          <TouchableOpacity style={styles.saveButton} onPress={handleSave}>
            <Text style={styles.saveButtonText}>{isEditing ? 'Update' : 'Save'}</Text>
          </TouchableOpacity>
          
          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
   backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(125),
    height: s(125),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
    paddingHorizontal: ms(30),
    textAlign: 'center',
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
  },
  scrollViewContent: {
    paddingBottom: ms(350),
  },
  radioGroup: {
    marginBottom: ms(20),
  },
  radioOption: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(20),
  },
  radioButton: {
    width: s(24),
    height: s(24),
    borderRadius: ms(12),
    borderWidth: ms(2),
    borderColor: '#1C2C4F',
    marginRight: ms(10),
    justifyContent: 'center',
    alignItems: 'center',
  },
  radioButtonSelected: {
    width: s(12),
    height: s(12),
    borderRadius: ms(6),
    backgroundColor: '#1C2C4F',
  },
  radioText: {
    fontSize: ms(18, 0.3),
    color: '#1C2C4F',
  },
  detailsInput: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(10),
    padding: ms(15),
    marginBottom: ms(20),
    minHeight: s(100),
    textAlignVertical: 'top',
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
  },
  structuredInput: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(10),
    padding: ms(15),
    marginBottom: ms(15),
    minHeight: s(80),
    textAlignVertical: 'top',
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  toggleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: ms(15),
    paddingVertical: ms(5),
  },
  toggleLabel: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    flex: 1,
    marginRight: ms(15),
  },
  inputLabel: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(8),
    marginTop: ms(5),
  },
  label: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(10),
  },
  specialInstructionsInput: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(10),
    padding: ms(15),
    marginBottom: ms(30),
    minHeight: s(150),
    textAlignVertical: 'top',
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
  },
  saveButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
    marginBottom: ms(30),
  },
  saveButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  detailsTitle: {
    fontSize: ms(16, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  bottomPadding: {
    height: s(120),
  },
});

export default BodyArrangementsInputScreen;
